/* global debounce */

(() => {
  const DEBUG = false;

  if (!customElements.get('predictive-search')) {
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.cachedResults = {};

        // fallback selector: cerca prima dentro il componente, poi a livello documento
        this.input = this.querySelector('.js-search-input') || document.querySelector('.js-search-input') || null;
        this.productTypeSelect = this.querySelector('.js-search-product-types');
        this.productTypeInput = document.getElementById('product_type_input');
        this.resetBtn = this.querySelector('.js-search-reset') || document.querySelector('.js-search-reset') || null;
        this.results = this.querySelector('.js-search-results') || this.querySelector('.predictive-search__results') || document.querySelector('.js-search-results') || document.querySelector('.predictive-search__results') || null;
        this.overlay = this.querySelector('.js-search-overlay') || document.querySelector('.js-search-overlay') || null;
        this.statusEl = this.querySelector('.js-search-status') || document.querySelector('.js-search-status') || null;
        this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';

        if (DEBUG) console.log('[PS] ctor', { input: this.input, results: this.results });

        this.addListeners();
      }

      disconnectedCallback() {
        this.close();
      }

      addListeners() {
        if (this.input) {
          this.input.addEventListener('focus', this.handleFocus.bind(this));
          this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
        }
        if (this.productTypeSelect) {
          this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
        }
        const form = this.querySelector('.search') || document.querySelector('.search');
        if (form) form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      getQuery() {
        return this.input ? this.input.value.trim() : '';
      }

      handleSubmit(evt) {
        if (
          !this.getQuery().length ||
          this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
        ) {
          evt.preventDefault();
        }
      }

      handleInput() {
        if (this.input) this.input.setAttribute('value', this.input.value);
        const searchTerm = this.getQuery();
        if (!searchTerm.length) {
          this.close();
          return;
        }
        this.getResults(searchTerm);
      }

      handleProductTypeChange(evt) {
        if (this.productTypeInput) this.productTypeInput.value = evt.detail.selectedValue;
        const query = this.getQuery();
        if (query.length > 0) {
          this.getResults(query);
        }
      }

      handleFocus() {
        const searchTerm = this.getQuery();
        if (!searchTerm.length) return;

        if (this.getAttribute('results') === 'true') {
          this.open();
        } else {
          this.getResults(searchTerm);
        }
      }

      handleKeydown(evt) {
        if (evt.target.matches('.tablist__tab')) return;

        switch (evt.key) {
          case 'ArrowUp':
          case 'ArrowDown':
            evt.preventDefault();
            if (this.hasAttribute('open')) this.handleResultsNav(evt.key);
            break;
          case 'Enter':
            this.selectOption();
            break;
          case 'Escape':
            this.close();
            break;
        }
      }

      handleKeyup() {
        if (!this.getQuery().length) this.close();
      }

      handleResultsNav(key) {
        const selectedResult = this.querySelector(
          '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
        );
        const allResults = this.querySelectorAll(
          '[role="tabpanel"]:not([hidden]) .predictive-search__item'
        );
        let resultToSelect = allResults[0];

        if (key === 'ArrowUp') {
          if (!selectedResult) return;
          resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
        } else if (key === 'ArrowDown') {
          if (selectedResult) resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }

        if (this.statusEl) this.statusEl.textContent = '';

        if (resultToSelect && resultToSelect !== selectedResult) {
          resultToSelect.setAttribute('aria-selected', 'true');
          if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');
          this.setLiveRegionText(resultToSelect.textContent);
          if (this.input) this.input.setAttribute('aria-activedescendant', resultToSelect.id);
        }
      }

      selectOption() {
        const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
        if (selectedResult) selectedResult.click();
      }

      async getResults(searchTerm) {
        this.setLiveRegionLoadingState();

        let searchFields = 'title,product_type,variants.title,vendor';
        if (window.theme && theme.settings && theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
        if (window.theme && theme.settings && theme.settings.pSearchIncludeTags) searchFields += ',tag';

        let searchParams = '';
        if (this.productTypeInput && this.productTypeInput.value !== '') {
          searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
        } else {
          searchParams = `q=${encodeURIComponent(searchTerm)}`;
        }

        searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
        searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
        searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
        searchParams += '&section_id=predictive-search';

        const queryKey = searchParams.replace(' ', '-').toLowerCase();
        if (this.cachedResults[queryKey]) {
          this.renderResults(this.cachedResults[queryKey]);
          return;
        }

        try {
          const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
          if (!response.ok) throw new Error(response.status);

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
          if (!resultsEl) {
            if (DEBUG) console.warn('[PredictiveSearch] resultsEl not found in fetched template');
            this.close();
            return;
          }

          // protezione: se input.id è vuoto non fare replace (evita di cancellare 'psearch')
          let resultsMarkup = '';
          if (this.input && this.input.id) {
            resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);
          } else {
            resultsMarkup = resultsEl.innerHTML;
          }

          // Filtra le collezioni locked presenti direttamente nel markup (collections panels)
          const tmpDiv = document.createElement('div');
          tmpDiv.innerHTML = resultsMarkup;
          tmpDiv.querySelectorAll('li.predictive-search__item[data-meta]').forEach((el) => {
            const metaRaw = el.dataset.meta || '';
            let meta = {};
            try { meta = JSON.parse(metaRaw); } catch(e) { meta = {}; }
            if (meta.locked === true || meta.is_locked === true) {
              if (DEBUG) console.log('[PredictiveSearch] Collezione esclusa (li data-meta):', el.dataset.title || '(senza titolo)');
              el.remove();
            }
          });
          resultsMarkup = tmpDiv.innerHTML;

          this.cachedResults[queryKey] = resultsMarkup;
          this.renderResults(resultsMarkup);
        } catch (error) {
          if (DEBUG) console.error('[PredictiveSearch] getResults error', error);
          this.close();
        }
      }

      setLiveRegionLoadingState() {
        this.setLiveRegionText(this.loadingText);
      }

      setLiveRegionText(statusText) {
        if (!this.statusEl) return;
        this.statusEl.setAttribute('aria-hidden', 'false');
        this.statusEl.textContent = statusText;
        setTimeout(() => this.statusEl.setAttribute('aria-hidden', 'true'), 1000);
      }

      // renderResults è dentro la classe (molto importante)
      renderResults(resultsMarkup) {
  if (!this.results) return;

  const temp = document.createElement('div');
  temp.innerHTML = resultsMarkup;

  // tutti i prodotti con handle
  const productLis = Array.from(temp.querySelectorAll('li.predictive-search__item[data-collection-handle]'));
  const handles = Array.from(new Set(productLis.map(li => li.dataset.collectionHandle).filter(Boolean)));

  if (handles.length === 0) {
    this.results.innerHTML = temp.innerHTML;
    this._finalizeRender();
    return;
  }

  const fetchLockForHandle = async (handle) => {
    try {
      const res = await fetch(`/collections/${handle}?view=meta`);
      const txt = await res.text();
      const json = JSON.parse(txt);
      return !!json?.locked;
    } catch {
      return false;
    }
  };

  Promise.all(handles.map(async h => [h, await fetchLockForHandle(h)]))
    .then(lockPairs => {
      const lockMap = Object.fromEntries(lockPairs);

      productLis.forEach(li => {
        const h = li.dataset.collectionHandle;
        if (h && lockMap[h]) li.remove();
      });

      // inserisci solo dopo aver rimosso i locked
      this.results.innerHTML = temp.innerHTML;
      this._finalizeRender();
    });
}


      // helper di finalizzazione (riapre pannello, inizializza tabs)
      _finalizeRender() {
        const remaining = this.results.querySelectorAll('li.predictive-search__item');
        if (DEBUG) console.log('[PredictiveSearch] remaining items after filtering:', remaining.length);

        if (!remaining.length) {
          this.close();
          return;
        }

        this.setAttribute('results', true);
        this.open();

        // inizializza tab custom se presenti
        const tabbedContents = this.results.querySelectorAll('tabbed-content');
        tabbedContents.forEach(tc => {
          const hasTabs = tc.querySelectorAll('[role="tab"]').length > 0;
          if (hasTabs) {
            try {
              if (window.Tabs && typeof window.Tabs === 'function') {
                new window.Tabs(tc);
              }
            } catch (e) {
              if (DEBUG) console.warn('[PredictiveSearch] Tabs init failed', e);
            }
          }
        });
      }

      open() {
        if (this.overlay) this.overlay.classList.add('is-visible');
        if (this.getQuery().length && this.resetBtn) this.resetBtn.hidden = false;
        if (this.input) this.input.setAttribute('aria-expanded', 'true');
        this.setAttribute('open', '');
        document.body.classList.add('overlay-predictive-search');

        this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
        this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
        this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
        this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

        this.addEventListener('keydown', this.keydownHandler);
        this.addEventListener('keyup', this.keyupHandler);
        if (this.resetBtn) this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
        if (this.overlay) this.overlay.addEventListener('click', this.overlayClickHandler);
      }

      close(evt) {
        if (evt && evt.target === this.resetBtn) {
          if (this.input) this.input.value = '';
          this.removeAttribute('results');
          if (this.input) this.input.focus();
        }

        const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
        if (selected) selected.setAttribute('aria-selected', 'false');

        this.removeAttribute('open');
        if (this.resetBtn) this.resetBtn.hidden = true;
        if (this.overlay) this.overlay.classList.remove('is-visible');
        if (this.input) {
          this.input.setAttribute('aria-activedescendant', '');
          this.input.setAttribute('aria-expanded', 'false');
        }
        document.body.classList.remove('overlay-predictive-search');

        this.removeEventListener('keydown', this.keydownHandler);
        if (this.resetBtn && this.resetBtnClickHandler) this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
        if (this.overlay && this.overlayClickHandler) this.overlay.removeEventListener('click', this.overlayClickHandler);
      }
    }

    customElements.define('predictive-search', PredictiveSearch);
  }
})();
